import React from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'
import capitalizeString from '../utils/capitalizeString'
// STATIC imports — deliberately NOT next/dynamic (see #130 note below).
import Shop from './sections/Shop'
import FaqPage from './sections/FaqPage'
import ProductCheckoutPage from './sections/Products/ProductCheckoutPage'
import UDFP20UpgradesCheckoutPage from './sections/Products/UDFP20UpgradesCheckoutPage'
import UpgradableWheelBaseCheckoutPage from './sections/Products/UpgradableWheelBaseCheckoutPage'
import Gt2CockpitCheckoutPage from './sections/Products/GT2CockpitCheckoutPage'
import Gt2FullRigCheckoutPage from './sections/Products/GT2FullRigCheckoutPage'
import Gt2SeatSpecs from './sections/Products/GT2SeatSpecs'
import SpecsPage from './sections/Products/SpecsPage'
import SaleHero from './sections/Sale/SaleHero'
import SaleLimitedDeals from './sections/Sale/SaleLimitedDeals'
import SaleDealsGrid from './sections/Sale/SaleDealsGrid'
import SaleInfo from './sections/Sale/SaleInfo'
// Turnkey configurator (TEST page /<region>/turnkey_test). Heavy interactive
// island rendered inside RegionPageClient -> STATIC import, never next/dynamic
// (#130: a React.lazy boundary here reorders the footer and breaks hydration).
import TurnkeyConfigurator from './sections/TurnkeyConfigurator'
// #134: the two remaining INTERACTIVE `'use client'` island sections — same
// #130 risk class as SpecsPage/FaqPage but for the product-marketing/overview
// pages. See the EXCEPTION note below.
import SecondaryNavbar from './sections/Products/SecondaryNavbar'
import ProductFaq from './sections/Products/ProductFaq'
// Internal-linking hub/spoke (Audit C H1). STATIC import (presentational, no
// next/dynamic — no #130 boundary). Appended after the page's sections; also
// registered in SectionComponents so a `relatedContent` _type would dispatch too.
import RelatedContent from './sections/RelatedContent'
import { resolveRelated } from './sections/RelatedContent/resolveRelated'
import { isSeasonalBannerSection } from '../lib/saleCampaign'

// Section registry — code-split via next/dynamic (Tier-1 first-load JS win).
//
// WHY: RenderSections resolves a page's section components by `_type` at
// runtime. Previously this was `import * as SectionComponents from './sections'`
// — a namespace import indexed by a computed key — which webpack cannot
// tree-shake, so ALL ~90 section components shipped in the first-load JS of
// EVERY route even though a page renders only a handful (e.g. /shop renders
// just `shop` + `faqPage`). Mapping each `_type` to a next/dynamic import
// splits every section into its own async chunk, so a route downloads JS for
// only the sections it actually renders.
//
// SSR IS PRESERVED: every dynamic entry keeps next/dynamic's default `ssr: true`,
// so each section is still fully server-rendered into the SSR HTML (above-the-
// fold and indexed content stays server-rendered — no SEO/first-paint
// regression, and — for the still-dynamic entries — no hydration mismatch). The
// win is purely per-route client-chunk splitting: the client loads the matching
// chunk before hydration, identical output.
//
// EXCEPTION — the STATIC-import entries below are NOT next/dynamic (#130/#134).
// RenderSections runs INSIDE the `'use client'` island RegionPageClient. Under
// production streaming SSR, next/dynamic's React.lazy synthesizes a Suspense
// STREAMING boundary (`<!--$?--><template id="B:n">…$RC(...)`); while a section
// is suspended server-side the site FOOTER is flushed early (before the
// section), but the client renders the section synchronously (chunk preloaded,
// no suspend) so its first-render tree has NO boundary → the footer sits in a
// different position → hydration mismatch (React #418 + #422). This was proven
// on /us/shop and /us/product/r295-steering-wheel.
//
// The static-import set is exactly the HEAVY INTERACTIVE `'use client'` island
// sections that render inside this island on pages that route through THIS
// client path (a page routes here whenever any of its sections lacks an RSC
// server twin — see app/[[...slug]]/RenderSectionsServer.jsx). Two groups:
//   • commerce/checkout + shop leaf pages (#130): `Shop`, `FaqPage`,
//     `ProductCheckoutPage`, `UDFP20UpgradesCheckoutPage`,
//     `UpgradableWheelBaseCheckoutPage`, `Gt2CockpitCheckoutPage`, `SpecsPage`.
//   • product-marketing/overview island sections (#134): `SecondaryNavbar`
//     (6 client-path overview pages: lite-formula-wheel, pedals, R295,
//     DFP15/DFP20/uDFP20 wheel bases — via a globalPageSection reference) and
//     `ProductFaq` (lite-formula-wheel, pedals). Both are genuine islands
//     (useAppState + scroll useEffect / FAQ state) and co-render on those
//     client-path pages alongside other dynamic() sections, so as next/dynamic
//     they synthesize the SAME footer-reorder boundary. They mirror
//     SpecsPage/FaqPage for the marketing/overview pages.
// `{ loading: () => null }` does NOT help (it keeps the boundary); only removing
// the lazy boundary via a static import does. These ship in the shared client
// chunk — a small, measured first-load-JS cost that also avoids the full client
// re-render the mismatch otherwise forces. The presentational dynamic() sections
// (LiteFormulaWheel*, r295TextImage, featuresOverview, …) are NOT this class —
// they have RSC server twins and no client interactivity, so they stay
// per-route code-split. The #132 ESLint guard pins this static set.
// #118 client-JS reduction — DEAD-ENTRY PRUNE (dataset audit 2026-07-07). The
// following section `_type`s were removed from this CLIENT registry because a
// full walk of content/dataset.ndjson (every node, any depth) found ZERO
// occurrences of them — they are never rendered by any route, yet each
// `dynamic(() => import(...))` entry was still bundled into the always-loaded
// section chunk (461) on EVERY catch-all page (the per-section next/dynamic
// splitting collapses into one eager chunk, so a dead entry is dead weight that
// ships site-wide). Pruned types (0 dataset occurrences):
//   productDetails, productOffer, teamVrsHome, collapsibleList, coachesList,
//   quotesCarousel, dataPacksCarousel, oldLogosShowcase, getFasterPricingTable,
//   pictureAndTextWithMailingList.
// This is REGISTRY-only (top-level section dispatch); any of these components
// still statically imported by another section keeps working via that import.
// If content ever re-introduces one of these `_type`s it renders as "Missing
// section {type}" (loud, immediate) — re-add the `dynamic()` line to restore it.
// The SERVER twins (OldLogosShowcase, TeamVrsHome in RenderSectionsServer) are
// unaffected (separate server-path registry).
const SectionComponents = {
  HeaderGallery1: dynamic(() => import('./sections/HeaderGallery1')),
  DownloadsPage: dynamic(() => import('./sections/DownloadsPage')),
  CenteredTwoSidedContent: dynamic(() => import('./sections/CenteredTwoSidedContent')),
  PictureAndText: dynamic(() => import('./sections/PictureAndText')),
  SimplePictureAndText: dynamic(() => import('./sections/SimplePictureAndText')),
  Video: dynamic(() => import('./sections/Video')),
  TripleImages: dynamic(() => import('./sections/TripleImages')),
  Shop, // static import (#130)
  RichText: dynamic(() => import('./sections/RichText')),
  ContactForm: dynamic(() => import('./sections/ContactForm')),
  TextAndSideImage: dynamic(() => import('./sections/TestAndSideImage')),
  TurnkeySolutions: dynamic(() => import('./sections/TurnkeySolutions')),
  TurnkeyConfigurator, // static import (#130 - heavy interactive island)
  SectionTitle: dynamic(() => import('./sections/SectionTitle')),
  SectionTitleV2: dynamic(() => import('./sections/SectionTitleV2')),
  PricingTable: dynamic(() => import('./sections/PricingTable')),
  PricingTableCoaching: dynamic(() => import('./sections/PricingTableCoaching')),
  AddOnsTable: dynamic(() => import('./sections/AddOnsTable')),
  FaqCollapsibleList: dynamic(() => import('./sections/FaqCollapsibleList')),
  FaqPage, // static import (#130)
  LegalDocumentPage: dynamic(() => import('./sections/LegalDocumentPage')),
  LegalDocumentsLinks: dynamic(() => import('./sections/LegalDocumentsLinks')),
  DataPacksList: dynamic(() => import('./sections/DataPacksList')),
  TwoSidedTextContent: dynamic(() => import('./sections/TwoSidedTextContent')),
  PedalsHero: dynamic(() => import('./sections/Products/PedalsHero')),
  PedalsShowcase: dynamic(() => import('./sections/Products/PedalsShowcase')),
  ProductImageAndSpecs: dynamic(() => import('./sections/Products/ProductImageAndSpecs')),
  FeaturesOverview: dynamic(() => import('./sections/Products/FeaturesOverview')),
  FfbNextGen: dynamic(() => import('./sections/Products/FfbNextGen')),
  ProductDescription: dynamic(() => import('./sections/Products/ProductDescription')),
  ProductTestimonials: dynamic(() => import('./sections/Products/ProductTestimonials')),
  ProductTestimonialsV2: dynamic(() => import('./sections/Products/ProductTestimonialsV2')),
  ProductTestimonialsV3: dynamic(() => import('./sections/Products/ProductTestimonialsV3')),
  ProductImageAndText: dynamic(() => import('./sections/Products/ProductImageAndText')),
  LiteWheelImageAndText: dynamic(() => import('./sections/Products/LiteFormulaWheel/LiteWheelImageAndText')),
  LiteWheelSpecs: dynamic(() => import('./sections/Products/LiteFormulaWheel/LiteWheelSpecs')),
  LiteFormulaWheelCompatibility: dynamic(() => import('./sections/Products/LiteFormulaWheel/LiteFormulaWheelCompatibility')),
  ConfigToolShowcase: dynamic(() => import('./sections/Products/ConfigToolShowcase')),
  ProductFaq, // static import (#134 — interactive island, #130 risk class)
  LiteFormulaWheelHero: dynamic(() => import('./sections/Products/LiteFormulaWheel/LiteFormulaWheelHero')),
  R295TextImage: dynamic(() => import('./sections/Products/R295/R295TextImage')),
  R295Customization: dynamic(() => import('./sections/Products/R295/R295Customization')),
  R295Pairing: dynamic(() => import('./sections/Products/R295/R295Pairing')),
  R295Specs: dynamic(() => import('./sections/Products/R295/R295Specs')),
  SecondaryNavbar, // static import (#134 — interactive island, #130 risk class)
  RevealScrollText: dynamic(() => import('./sections/RevealScrollText')),
  ProductCheckoutPage, // static import (#130)
  UDFP20UpgradesCheckoutPage, // static import (#130)
  UpgradableWheelBaseCheckoutPage, // static import (#130)
  Gt2CockpitCheckoutPage, // static import (#130)
  Gt2FullRigCheckoutPage, // static import (#130)
  Gt2SeatSpecs, // static import (#130) — band under the GT-2 Seat buy box
  Page404: dynamic(() => import('./sections/Pages/Page404')),
  OrderConfirmationPage: dynamic(() => import('./sections/OrderConfirmationPage')),
  OrderShippingInfoPage: dynamic(() => import('./sections/OrderShippingInfoPage')),
  SpecsPage, // static import (#130)
  BlogPage: dynamic(() => import('./sections/Blog/BlogPage')),
  BlogPostPage: dynamic(() => import('./sections/Blog/BlogPostPage')),
  TeamVrsHero: dynamic(() => import('./sections/TeamVrs/TeamVrsHero')),
  TeamVrsFeatures: dynamic(() => import('./sections/TeamVrs/TeamVrsFeatures')),
  TeamVrsPricing: dynamic(() => import('./sections/TeamVrs/TeamVrsPricing')),
  TeamVrsNascarPage: dynamic(() => import('./sections/TeamVrsNascarPage')),
  AboutUsPage: dynamic(() => import('./sections/Pages/AboutUsPage')),
  PaymentPage: dynamic(() => import('./sections/Pages/PaymentPage')),
  HardwarePage: dynamic(() => import('./sections/Pages/HardwarePage')),
  HardwarePageShowcase: dynamic(() => import('./sections/Pages/HardwarePage/HardwarePageShowcase')),
  BF2024Page: dynamic(() => import('./sections/BF2024Page')),
  BF2024Hero: dynamic(() => import('./sections/BF2024Hero')),
  // Black Friday 2026 (lib/saleCampaign) — interactive islands, STATIC imports (#130)
  SaleHero,
  SaleLimitedDeals,
  SaleDealsGrid,
  SaleInfo,
  CreatorsProgramPage: dynamic(() => import('./sections/CreatorsProgram/CreatorsProgramPage')),
  ProductOverviewHero: dynamic(() => import('./sections/Products/Utility/ProductOverviewHero')),
  UDFP20Footer: dynamic(() => import('./sections/Products/UDFP20/UDFP20Footer')),
  GoogleFormEmbed: dynamic(() => import('./sections/GoogleFormEmbed')),
  ConversionCta: dynamic(() => import('./sections/ConversionCta')),
  HomeSection: dynamic(() => import('./sections/Home/HomeSection')),
  HomeSectionDoubled: dynamic(() => import('./sections/Home/HomeSectionDoubled')),
  HomeSectionDoubledV2: dynamic(() => import('./sections/Home/HomeSectionDoubledV2')),
  GetFasterHero: dynamic(() => import('./sections/GetFaster/GetFasterHero')),
  GetFasterStats: dynamic(() => import('./sections/GetFaster/GetFasterStats')),
  GetFasterAudience: dynamic(() => import('./sections/GetFaster/GetFasterAudience')),
  GetFasterTestimonials: dynamic(() => import('./sections/GetFaster/GetFasterTestimonials')),
  GetFasterHowItWorks: dynamic(() => import('./sections/GetFaster/GetFasterHowItWorks')),
  GetFasterWhyUs: dynamic(() => import('./sections/GetFaster/GetFasterWhyUs')),
  GetFasterPricing: dynamic(() => import('./sections/GetFaster/GetFasterPricing')),
  StoryText: dynamic(() => import('./sections/StoryText')),
  Gt2ColorSwatches: dynamic(() => import('./sections/Gt2ColorSwatches')),
  ComparisonTable: dynamic(() => import('./sections/ComparisonTable')),
  BuiltInEuBadge: dynamic(() => import('./sections/BuiltInEuBadge')),
  RelatedContent, // static import (presentational internal-linking block)
}

function resolveSections(section) {
  const Section = SectionComponents[capitalizeString(section._type)]

  if (Section) {
    return Section
  }

  console.error('Cant find section', section) // eslint-disable-line no-console
  return null
}

function RenderSections(props) {
  const { sections, latestStories, slug = null, region = null } = props

  if (!sections) {
    console.error('Missing section')
    return <div>Missing sections</div>
  }

  // Audit C H1 — append the internal-linking block for product/hardware/blog
  // pages (null for everything else, so non-product pages are unchanged).
  const related = resolveRelated({ slug, sections, region })

  // Single-<h1> rule (Audit A #1): the FIRST non-hidden section is the page hero.
  // We tag it with `isPageHero` so a section that renders a title-as-<h1> only on
  // the hero (HomeSection — the home page has several homeSections but must carry
  // exactly one <h1>) can promote its title. Sections that don't consume the prop
  // ignore it (same harmless-extra-prop contract as `latestStories`). Templates
  // whose primary heading is a fixed single section (checkout, blog, pedals) use
  // the `<Text primary>` contract directly and don't depend on this flag. The
  // seasonal homepage sale banner is skipped (it titles itself with an <h2>).
  const firstVisibleKey = sections.find((s) => s && !s.hidden && !isSeasonalBannerSection(s))?._key

  return (
    <div className="flex flex-col">
      {sections.map((section) => {
        if (section.hidden) {
          return null
        }
        const SectionComponent = resolveSections(section)
        if (!SectionComponent) {
          return <div key={section._key}>Missing section {section._type}</div>
        }
        return <SectionComponent {...section} key={section._key} slug={slug} latestStories={latestStories} isPageHero={section._key === firstVisibleKey} />
      })}
      {related && <RelatedContent {...related} />}
    </div>
  )
}

RenderSections.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      _type: PropTypes.string,
      _key: PropTypes.string,
      section: PropTypes.instanceOf(PropTypes.object),
    })
  ),
  slug: PropTypes.string,
  region: PropTypes.string,
}

export default RenderSections
